// API client for the new flat Load primitive (RE-Project dual).
// Endpoints: /api/v1/loads/*

import { fetchWithAuth } from "./http";
import { API } from "./api";
import type {
  LoadCreateInput,
  LoadDetail,
  LoadOffset,
  LoadRun,
  LoadSummary,
} from "../features/load/domain/types";
import type {
  AirshedFeatureCollection,
  IntensityTileResponse,
} from "../features/model/domain/types";

const BASE = "/loads";

export const listLoads = (token: string) =>
  fetchWithAuth<LoadSummary[]>(BASE, token);

export const getLoad = (loadId: string, token: string) =>
  fetchWithAuth<LoadDetail>(`${BASE}/${loadId}`, token);

export const createLoad = (payload: LoadCreateInput, token: string) =>
  fetchWithAuth<LoadDetail>(BASE, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const updateLoad = (
  loadId: string,
  patch: Partial<LoadCreateInput>,
  token: string,
) =>
  fetchWithAuth<LoadDetail>(`${BASE}/${loadId}`, token, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });

export const deleteLoad = (loadId: string, token: string) =>
  fetchWithAuth<{ message: string }>(`${BASE}/${loadId}`, token, {
    method: "DELETE",
  });

// Offsets
export const addOffset = (
  loadId: string,
  payload: Partial<LoadOffset>,
  token: string,
) =>
  fetchWithAuth<LoadOffset>(`${BASE}/${loadId}/offsets`, token, {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const updateOffset = (
  loadId: string,
  offsetId: string,
  patch: Partial<LoadOffset>,
  token: string,
) =>
  fetchWithAuth<LoadOffset>(`${BASE}/${loadId}/offsets/${offsetId}`, token, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });

export const deleteOffset = (loadId: string, offsetId: string, token: string) =>
  fetchWithAuth<{ message: string }>(
    `${BASE}/${loadId}/offsets/${offsetId}`,
    token,
    { method: "DELETE" },
  );

// Queue / runs
export const queueLoadRun = (
  loadId: string,
  token: string,
  scenarioYear?: number,
) =>
  fetchWithAuth<{ run_id: string; scenario_year: number }>(
    `${BASE}/${loadId}/queue`,
    token,
    {
      method: "POST",
      body: JSON.stringify(scenarioYear ? { scenario_year: scenarioYear } : {}),
    },
  );

export const getLatestLoadRun = (loadId: string, token: string) =>
  fetchWithAuth<LoadRun>(`${BASE}/${loadId}/runs/latest`, token);

// A4 spatial: per-plant consumption attribution for the latest run.
export interface LoadPlantConsumption {
  plant_id: string;
  plant_name: string | null;
  fuel_type: string;
  ba_code: string | null;
  state: string | null;
  county: string | null;
  latitude: number | null;
  longitude: number | null;
  capacity_mw: number | null;
  consumed_mwh: number;
}

export const getLoadPlants = (loadId: string, token: string) =>
  fetchWithAuth<{
    load_id: string;
    run_id: string | null;
    scenario_year: number | null;
    count: number;
    plants: LoadPlantConsumption[];
  }>(`${BASE}/${loadId}/plants`, token);

// L4: Dispersion — InMAP airshed polygons for the load's displaced plants.
// Same shape as the RE-project equivalent (AirshedFeatureCollection).
export const fetchLoadAirsheds = (loadId: string, token: string) =>
  fetchWithAuth<AirshedFeatureCollection>(`${BASE}/${loadId}/airsheds`, token);

// Intensity tiles — same XYZ-tile shape as projects. Returns null on 404
// (Phase 4b rebuild not run for this load yet); frontend falls back to
// the airshed polygon layer.
export async function fetchLoadIntensityTiles(
  loadId: string,
  token: string,
): Promise<IntensityTileResponse | null> {
  const r = await fetch(`${API}${BASE}/${loadId}/airshed-intensity-tiles`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (r.status === 401) {
    const { handleExpiredSession } = await import("./http");
    handleExpiredSession();
    return null;
  }
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`intensity-tiles ${r.status}`);
  return r.json();
}

// Per-level aggregate (state / county / cbsa) GeoJSON. Returns null on 404.
export type IntensityAggregateLevel = "state" | "county" | "cbsa";

export async function fetchLoadIntensityAggregate(
  loadId: string,
  level: IntensityAggregateLevel,
  token: string,
): Promise<any | null> {
  const r = await fetch(
    `${API}${BASE}/${loadId}/airshed-intensity-aggregate?level=${level}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (r.status === 401) {
    const { handleExpiredSession } = await import("./http");
    handleExpiredSession();
    return null;
  }
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`intensity-aggregate ${r.status}`);
  return r.json();
}
